// src/components/PatientAutofill.js
// A compact "autofill from saved patient" widget.
// Shows a dropdown of the user's saved patient records; on selection it calls onSelect(patient).
// Drops in above any form section — just pass the right onSelect mapper.

import React, { useEffect, useRef, useState } from 'react';
import { getMyPatients } from '../services/patientApi';

const RELATION_EMOJI = {
  SELF:    '🧑',
  SPOUSE:  '💑',
  CHILD:   '👶',
  PARENT:  '👴',
  SIBLING: '👫',
  OTHER:   '👤',
};

const initials = (name = '') =>
  name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase()).filter(Boolean).join('') || '?';

/**
 * @param {Object}   props
 * @param {Function} props.onSelect   Called with a PatientResponse when user picks one.
 * @param {string}   [props.label]    Override the default label.
 */
const PatientAutofill = ({ onSelect, label = 'Autofill from saved profile' }) => {
  const [patients, setPatients] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [open,     setOpen]     = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);

  // Load once on mount
  useEffect(() => {
    getMyPatients()
      .then(setPatients)
      .catch(() => setPatients([]))
      .finally(() => setLoading(false));
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading || patients.length === 0) return null;

  const handlePick = (p) => {
    setSelected(p);
    setOpen(false);
    onSelect(p);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: 20 }}>
      {/* Trigger row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: 12,
          border: '1.5px dashed #a5b4fc',
          background: '#f5f3ff',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background .15s',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ fontSize: 18 }}>⚡</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13, color: '#4f46e5' }}>
            {label}
          </div>
          {selected ? (
            <div style={{ fontSize: 11.5, color: '#6d28d9', marginTop: 1 }}>
              Filled from: <strong>{selected.fullName}</strong>
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: '#7c3aed', marginTop: 1 }}>
              {patients.length} saved profile{patients.length !== 1 ? 's' : ''} available
            </div>
          )}
        </div>
        {/* Chevron */}
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          right: 0,
          zIndex: 200,
          background: '#fff',
          border: '1.5px solid #e0e7ff',
          borderRadius: 14,
          boxShadow: '0 12px 40px rgba(79,70,229,.12)',
          overflow: 'hidden',
          animation: 'fadeDropdown .15s ease both',
        }}>
          <style>{`
            @keyframes fadeDropdown {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          <div style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontFamily: 'DM Sans, sans-serif',
            borderBottom: '1px solid #f3f4f6',
          }}>
            Select a profile to autofill
          </div>

          {patients.map((p) => {
            const name = p.fullName || 'Unknown';
            const rel  = p.relation || (p.isSelf ? 'SELF' : 'OTHER');
            const emoji = RELATION_EMOJI[rel] || '👤';
            return (
              <div
                key={p.id}
                onClick={() => handlePick(p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f9fafb',
                  transition: 'background .1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f3ff')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '')}
              >
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #eef2ff, #ede9fe)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#4f46e5',
                  fontFamily: 'Sora, sans-serif',
                }}>
                  {initials(name)}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Sora, sans-serif', fontWeight: 700,
                    fontSize: 13, color: '#1e1b4b', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {name}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 1 }}>
                    {emoji} {rel.charAt(0) + rel.slice(1).toLowerCase()}
                    {p.age ? ` · ${p.age} yrs` : ''}
                    {p.phone ? ` · ${p.phone}` : ''}
                  </div>
                </div>

                {/* Autofill badge */}
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: '#4f46e5', background: '#eef2ff',
                  padding: '3px 9px', borderRadius: 9999, flexShrink: 0,
                }}>
                  Fill ↗
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PatientAutofill;